import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, Subscription, takeUntil } from 'rxjs';
import {
  SegmentMemberResponse,
  SegmentPreviewContactResponse,
  SegmentBulkActionRequest,
} from '../../../models/segment.model';
import { SegmentService } from '../../../services/segment-api.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { ConfirmDialogService } from '../../../../../shared/services/confirm-dialog.service';
import { DatetimePipe } from '../../../../../shared/pipe/datetime.pipe';
import { AuthService } from '../../../../../pages/auth-pages/services/auth.service';

@Component({
  selector: 'app-segment-static-preview',
  standalone: true,
  imports: [CommonModule, DatetimePipe],
  templateUrl: './segment-static-preview.component.html',
})
export class SegmentStaticPreviewComponent implements OnChanges, OnDestroy {

  // ── Filter preview inputs (create mode) ──
  @Input() rulesLength = 0;
  @Input() contactosPreview: SegmentPreviewContactResponse[] = [];
  @Input() totalContactosPreview = 0;
  @Input() porcentajePreview = 0;
  @Input() totalContactosCrm = 0;
  @Input() isLoadingPreview = false;
  @Input() previewPage = 0;
  @Input() previewTotalPages = 0;
  @Input() contactosAExcluirIds: Set<number> = new Set();

  // ── Context inputs ──
  @Input() segmentId: number | null = null;
  @Input() isEditing = false;
  @Input() filtersModified = false;

  // ── Not-matching inputs (edit mode) ──
  @Input() contactosNoCoinciden: SegmentPreviewContactResponse[] = [];
  @Input() totalNoCoinciden = 0;
  @Input() isLoadingNoCoinciden = false;
  @Input() noCoincidentPage = 0;
  @Input() noCoincidentTotalPages = 0;

  @Input() drawerTab: 'members' | 'match' | 'no-match' = 'members';

  @Output() pageChange = new EventEmitter<number>();
  @Output() noCoincidentPageChange = new EventEmitter<number>();
  @Output() excludeToggle = new EventEmitter<number>();
  @Output() segmentGone = new EventEmitter<void>();
  @Output() memberCountChange = new EventEmitter<number>();

  // ── Member list state (edit mode) ──
  miembros: SegmentMemberResponse[] = [];
  selectedMembershipIds: number[] = [];
  isLoadingMembers = false;
  membersPage = 0;
  membersTotalPages = 0;
  membersTotalElements = 0;
  readonly membersPageSize = 20;
  isExecutingBulk = false;

  // ── Excluded members state ──
  miembrosExcluidos: SegmentMemberResponse[] = [];
  selectedExcludedContactIds: number[] = [];
  isLoadingExcluidos = false;
  excluidosPage = 0;
  excluidosTotalPages = 0;
  excluidosTotalElements = 0;
  showExcluidos = false;
  isReincluding = false;
  private excludedLoadSub?: Subscription;

  private readonly destroy$ = new Subject<void>();
  private memberLoadSub?: Subscription;

  constructor(
    private segmentService: SegmentService,
    private toast: ToastService,
    private confirmDialog: ConfirmDialogService,
    private authService: AuthService,
  ) {}

  get membersPercentage(): number {
    if (!this.totalContactosCrm) return 0;
    return Math.round((this.membersTotalElements / this.totalContactosCrm) * 1000) / 10;
  }

  /** true en modo edición con segmento existente → muestra lista de miembros con bulk actions */
  get showMemberList(): boolean {
    return this.isEditing && (this.segmentId ?? 0) > 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['segmentId'] && !changes['isEditing']) return;

    if (this.showMemberList) {
      this.resetMemberList();
      this.cargarMiembros(0);
    } else {
      this.resetMemberList();
    }
  }

  ngOnDestroy(): void {
    this.memberLoadSub?.unsubscribe();
    this.excludedLoadSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resetMemberList(): void {
    this.memberLoadSub?.unsubscribe();
    this.excludedLoadSub?.unsubscribe();
    this.miembros = [];
    this.selectedMembershipIds = [];
    this.membersPage = 0;
    this.membersTotalElements = 0;
    this.membersTotalPages = 0;
    this.isExecutingBulk = false;
    this.miembrosExcluidos = [];
    this.selectedExcludedContactIds = [];
    this.excluidosPage = 0;
    this.excluidosTotalElements = 0;
    this.excluidosTotalPages = 0;
    this.showExcluidos = false;
    this.isReincluding = false;
  }

  // ── Member list ──

  cargarMiembros(page: number): void {
    if (!this.segmentId) return;
    this.memberLoadSub?.unsubscribe();
    this.isLoadingMembers = true;
    this.membersPage = page;
    this.memberLoadSub = this.segmentService.getMembers(this.segmentId, page, this.membersPageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.miembros = res.content.filter(m => !m.isExcluded);
          this.membersTotalElements = res.totalElements;
          this.membersTotalPages = res.totalPages;
          this.selectedMembershipIds = [];
          this.isLoadingMembers = false;
          this.memberCountChange.emit(res.totalElements);
        },
        error: (err: any) => {
          this.isLoadingMembers = false;
          if (err?.status === 404) {
            this.segmentGone.emit();
          } else {
            this.toast.error({ title: 'Error', message: 'No se pudieron cargar los miembros del segmento.' });
          }
        },
      });
  }

  // ── Excluded members ──

  toggleShowExcluidos(): void {
    this.showExcluidos = !this.showExcluidos;
    if (this.showExcluidos && this.miembrosExcluidos.length === 0) {
      this.cargarExcluidos(0);
    }
  }

  cargarExcluidos(page: number): void {
    if (!this.segmentId) return;
    this.excludedLoadSub?.unsubscribe();
    this.isLoadingExcluidos = true;
    this.excluidosPage = page;
    this.excludedLoadSub = this.segmentService.getExcludedMembers(this.segmentId, page, this.membersPageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.miembrosExcluidos = res.content;
          this.excluidosTotalElements = res.totalElements;
          this.excluidosTotalPages = res.totalPages;
          this.selectedExcludedContactIds = [];
          this.isLoadingExcluidos = false;
        },
        error: () => {
          this.isLoadingExcluidos = false;
          this.toast.error({ title: 'Error', message: 'No se pudieron cargar los miembros excluidos.' });
        },
      });
  }

  toggleExcluido(contactId: number): void {
    const idx = this.selectedExcludedContactIds.indexOf(contactId);
    if (idx === -1) this.selectedExcludedContactIds.push(contactId);
    else this.selectedExcludedContactIds.splice(idx, 1);
  }

  toggleAllExcluidos(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.selectedExcludedContactIds = checked ? this.miembrosExcluidos.map(m => m.contact.contactId) : [];
  }

  isExcluidoSelected(contactId: number): boolean {
    return this.selectedExcludedContactIds.includes(contactId);
  }

  get isAllExcluidosSelected(): boolean {
    return this.miembrosExcluidos.length > 0 && this.selectedExcludedContactIds.length === this.miembrosExcluidos.length;
  }

  async ejecutarReincluir(): Promise<void> {
    if (!this.segmentId || this.selectedExcludedContactIds.length === 0) return;

    const n = this.selectedExcludedContactIds.length;
    const confirmed = await this.confirmDialog.confirm({
      title: 'Re-incluir miembros',
      message: `${n} contacto${n !== 1 ? 's' : ''} seleccionado${n !== 1 ? 's' : ''} volverán a ser miembros activos del segmento.`,
      confirmText: 'Sí, re-incluir',
      cancelText: 'Cancelar',
      variant: 'success',
    });

    if (!confirmed) return;

    this.isReincluding = true;
    const userId = this.authService.getCurrentUser()?.id ?? 0;

    this.segmentService.reincludeMembers(this.segmentId, [...this.selectedExcludedContactIds], userId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.isReincluding = false;
          const reactivated = res.reactivated ?? n;
          this.toast.success({
            title: 'Re-incluidos',
            message: `${reactivated} contacto${reactivated !== 1 ? 's' : ''} re-incluido${reactivated !== 1 ? 's' : ''} correctamente.`,
          });
          this.cargarMiembros(0);
          this.cargarExcluidos(0);
        },
        error: () => {
          this.isReincluding = false;
          this.toast.error({ title: 'Error', message: 'No se pudo completar la operación de re-inclusión.' });
        },
      });
  }

  previousExcluidosPage(): void {
    if (this.excluidosPage > 0) this.cargarExcluidos(this.excluidosPage - 1);
  }

  nextExcluidosPage(): void {
    if (this.excluidosPage < this.excluidosTotalPages - 1) this.cargarExcluidos(this.excluidosPage + 1);
  }

  // ── Selection ──

  toggleAll(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.selectedMembershipIds = checked ? this.miembros.map(m => m.id) : [];
  }

  toggleMember(id: number): void {
    const idx = this.selectedMembershipIds.indexOf(id);
    if (idx === -1) this.selectedMembershipIds.push(id);
    else this.selectedMembershipIds.splice(idx, 1);
  }

  isMemberSelected(id: number): boolean {
    return this.selectedMembershipIds.includes(id);
  }

  get isAllSelected(): boolean {
    return this.miembros.length > 0 && this.selectedMembershipIds.length === this.miembros.length;
  }

  // ── Bulk actions ──

  async ejecutarBulkAction(action: 'EXCLUDE' | 'DELETE'): Promise<void> {
    if (!this.segmentId || this.selectedMembershipIds.length === 0) return;

    const n = this.selectedMembershipIds.length;
    const label = `${n} contacto${n !== 1 ? 's' : ''} seleccionado${n !== 1 ? 's' : ''}.`;
    const isExclude = action === 'EXCLUDE';

    const confirmed = await this.confirmDialog.confirm({
      title: isExclude ? 'Excluir miembros' : 'Eliminar miembros',
      message: isExclude
        ? `Los contactos seleccionados quedarán excluidos del segmento. Su registro se conservará como excluido. ${label}`
        : `Los contactos seleccionados serán eliminados permanentemente del segmento. ${label}`,
      confirmText: isExclude ? 'Sí, excluir' : 'Sí, eliminar',
      cancelText: 'Cancelar',
      variant: isExclude ? 'warning' : 'danger',
    });

    if (!confirmed) return;

    this.isExecutingBulk = true;
    const request: SegmentBulkActionRequest = {
      action,
      memberIds: [...this.selectedMembershipIds],
    };

    this.segmentService.executeBulkAction(this.segmentId, request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.isExecutingBulk = false;
          const verb = isExclude ? 'excluidos' : 'eliminados';
          this.toast.success({
            title: 'Acción completada',
            message: `${res.processed} miembro(s) ${verb} correctamente.`,
          });
          this.cargarMiembros(0);
          if (isExclude && this.showExcluidos) this.cargarExcluidos(0);
        },
        error: () => {
          this.isExecutingBulk = false;
          this.toast.error({ title: 'Error', message: 'No se pudo ejecutar la acción masiva.' });
        },
      });
  }

  // ── Pagination (member list) ──

  previousMembersPage(): void {
    if (this.membersPage > 0) this.cargarMiembros(this.membersPage - 1);
  }

  nextMembersPage(): void {
    if (this.membersPage < this.membersTotalPages - 1) this.cargarMiembros(this.membersPage + 1);
  }
}
